import React, {useEffect, useState} from "react";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import {Col, Row} from "reactstrap";
import Divider from "@mui/material/Divider";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import {deepEqual} from "../../../../core/services/utils/utils";
import Dialog from "../../base/dialog";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import {CheckboxForm} from "../../base/checkbox/checkbox";
import {useTranslation} from "react-i18next";
import {Box, Tooltip} from "@mui/material";
import {InfoOutlined} from "@mui/icons-material";


const formNames = {
    code: 'code',
    title: 'title',
    noCarrierFee: 'noCarrierFee',
}

const schema = Yup.object().shape({
    [formNames.code]: Yup.string().trim().nullable().required(ValidateMessages.required).max(10, ValidateMessages.maxLength('Code', 10)),
    [formNames.title]: Yup.string().trim().nullable().required(ValidateMessages.required).max(20, ValidateMessages.maxLength('Title', 30)),
    [formNames.noCarrierFee]: Yup.boolean().nullable(),
});

const validate = makeValidate(schema);
const required = makeRequired(schema);

const UpsertFreightCompanyDialog = ({open, setOpen, data, onAdd}) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.upsert-freight-company-dialog'});
    const [loading, setLoading] = useState(false);
    const [initialValues, setInitialValues] = useState({});
    const isMounted = useIsMounted();

    /**
     * Listens to the changes of [data] in the props:
     * With each change, sets the [initialValues] in the state
     */
    useEffect(() => {
        setInitialValues({
            [formNames.code]: data?.code ?? undefined,
            [formNames.title]: data?.title ?? undefined,
            [formNames.noCarrierFee]: !!data?.noCarrierFee,
        })
    }, [
        data?.code,
        data?.title,
        data?.noCarrierFee,
    ])

    /**
     * creates a new company or updates the [data] prop by calling the api regarding this action
     */
    const onSubmit = async (values) => {
        setLoading(true);
        if (!!data) {
            await updateFreightCompany(values);
        } else {
            await createFreightCompany(values);
        }
        setOpen(false);
    }

    /**
     * Updates the selected company by calling the api regarding this action
     * @param values
     * @return {Promise<void>}
     * @private
     */
    const updateFreightCompany = async (values) => {
        const forApi = {
            id: data.id,
            code: values[formNames.code],
            title: values[formNames.title],
            noCarrierFee: !!values[formNames.noCarrierFee],
            updatedProperties: ['Title', 'Code', 'NoCarrierFee'],
        };
        const response = await Api.updateFreightCompanies(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            onAdd(response.data);
        }
        setLoading(false);
    }

    /**
     * Creates a new company in the system by calling the api regarding this action
     * @param values
     * @return {Promise<void>}
     * @private
     */
    const createFreightCompany = async (values) => {
        const forApi = {
            code: values[formNames.code],
            title: values[formNames.title],
            noCarrierFee: !!values[formNames.noCarrierFee],
        };
        const response = await Api.createFreightCompanies(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            onAdd(response.data);
        }
        setLoading(false);
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={setOpen}
                maxWidth={'sm'}
            >
                <Row className='no-gutters align-items-center w-100 transition'>
                    <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                        <p className='text-lg text-color-1 font-weight-semi-bold text-center'>
                            {data !== null
                                ? t('editTitle')
                                : t('addTitle')
                            }
                        </p>
                    </Row>
                    <Divider className='divider'/>
                    <div className='my-3 w-100'/>
                    <Form
                        className='w-100'
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={initialValues}
                        render={({initialValues: formInitialValues, values, form}) => {
                            return (
                                <Row className='no-gutters justify-content-around w-100'>
                                    <Row className='no-gutters justify-content-around w-85'>
                                        <Col xs={12} className={'mb-3'}>
                                            <OutlinedInputForm
                                                label={t('code')}
                                                name={formNames.code}
                                                type={'text'}
                                                required={required[formNames.code]}
                                                value={values[formNames.code] ?? ''}
                                                onChange={(e) => form.change(e.target.name, e.target.value)}
                                            />
                                        </Col>
                                        <Col xs={12} className={'mb-3'}>
                                            <OutlinedInputForm
                                                label={t('title')}
                                                name={formNames.title}
                                                type={'text'}
                                                required={required[formNames.title]}
                                                value={values[formNames.title] ?? ''}
                                                onChange={(e) => form.change(e.target.name, e.target.value)}
                                            />
                                        </Col>
                                        <Col xs={12} className={'mb-3'}>
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                <CheckboxForm
                                                    formControlLabelProps={{sx: {margin: 0}}}
                                                    name={formNames.noCarrierFee}
                                                    data={{
                                                        value: values[formNames.noCarrierFee],
                                                        label: t('noCarrierFee'),
                                                    }}
                                                />
                                                <Tooltip title={t('noCarrierFeesTooltip')} placement={'top'}>
                                                    <Box component={'span'}>
                                                        <InfoOutlined color="primary"/>
                                                    </Box>
                                                </Tooltip>
                                            </Box>
                                        </Col>
                                    </Row>
                                    <div className='my-3 w-100'/>
                                    <Divider className='divider'/>
                                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                                        <Button
                                            disabled={!open || loading}
                                            variant={'dialog'}
                                            color={'primary'}
                                            onClick={() => setOpen(false)}
                                            borderRadius={'0 8px 0 8px'}
                                        >
                                            {loading ? t('loading') : t('close')}
                                        </Button>
                                        <Button
                                            disabled={
                                                !open ||
                                                loading ||
                                                (initialValues !== null && deepEqual(formInitialValues, values))
                                            }
                                            variant={'dialog'}
                                            color={'primary'}
                                            type={'submit'}
                                            borderRadius={'8px 0 8px 0'}
                                        >
                                            {loading ? t('loading') : data !== null ? t('edit') : t('add')}
                                        </Button>
                                    </Row>
                                </Row>
                            );
                        }}
                    />
                </Row>
            </Dialog>
        </>
    );
}

export default UpsertFreightCompanyDialog;
